import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Panel, SectionHead, SectionShell, SignalSkeleton, StateChip } from "./_shared";
import { TrendingUp, TrendingDown, ChevronDown, ArrowRight } from "lucide-react";

const clamp = (n) => Math.max(3, Math.min(100, Math.round(n)));

function Sparkline({ points }) {
  if (!points || points.length < 2) return null;
  const vals = points.map((p) => p.revenue || 0);
  const max = Math.max(...vals, 1), min = Math.min(...vals, 0);
  const W = 240, H = 40, span = max - min || 1;
  const step = W / (points.length - 1);
  const d = vals.map((v, i) => `${i * step},${H - ((v - min) / span) * (H - 4) - 2}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-10" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id="spark" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7C5CFC" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#7C5CFC" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${H} ${d} ${W},${H}`} fill="url(#spark)" />
      <polyline points={d} fill="none" stroke="#7C5CFC" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export function BusinessPulse({ canReports, lowData }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);
  const dash = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });
  const health = useQuery({
    queryKey: ["business-health"], enabled: canReports,
    queryFn: async () => (await api.get("/reports/business-health")).data,
  });
  const q = { ...dash, isLoading: dash.isLoading || (canReports && health.isLoading) };

  return (
    <Panel className="p-5 sm:p-6" data-testid="business-pulse">
      <SectionHead eyebrow="Business Pulse" title="What's changing in your business"
        hint="Real signals from your live data — tap any to see what's behind it." />
      <SectionShell query={q} skeleton={<div className="py-2"><SignalSkeleton lines={4} /></div>}>
        {(d) => {
          const h = health.data || {};
          const rev = d.revenue || {}; const bk = d.bookings || {}; const cu = d.customers || {};
          const collected = rev.month || 0; const outstanding = rev.outstanding || 0;
          const collRatio = collected + outstanding > 0 ? (collected / (collected + outstanding)) * 100 : null;
          const completedToday = bk.completed_today; // may be undefined
          const hasPrev = typeof h.revenue_prev_month === "number" && h.revenue_prev_month > 0;
          const growth = typeof h.revenue_growth_pct === "number" ? h.revenue_growth_pct : null;

          // momentum is a TREND, not a ratio bar
          const momentum = {
            key: "momentum", kind: "trend",
            growth: hasPrev ? growth : null,
            detail: hasPrev ? `This month ${inr(h.revenue_month ?? collected)} vs last month ${inr(h.revenue_prev_month)}.`
              : "Not enough previous-period data to calculate momentum yet.",
            to: "/reports",
          };
          const ratios = [
            {
              key: "collections", label: "Collections",
              value: collRatio !== null ? clamp(collRatio) : null,
              caption: collRatio !== null ? `${inr(collected)} collected · ${inr(outstanding)} outstanding` : "Limited data",
              detail: `${inr(collected)} collected this month against ${inr(outstanding)} still outstanding.`, to: "/invoices",
            },
            {
              key: "retention", label: "Customer retention",
              value: typeof h.repeat_customer_rate === "number" ? clamp(h.repeat_customer_rate) : null,
              caption: typeof h.repeat_customer_rate === "number" ? `${h.repeat_customer_rate}% book more than once` : "Limited data",
              detail: `${h.repeat_customer_rate ?? 0}% of ${h.total_customers ?? cu.total ?? 0} customers have booked more than once.`, to: "/customers",
            },
            {
              key: "utilization", label: "Package utilization",
              value: typeof h.package_utilization_pct === "number" ? clamp(h.package_utilization_pct) : null,
              caption: typeof h.package_utilization_pct === "number" ? `${h.package_utilization_pct}% of package units used` : "Limited data",
              detail: "Share of purchased package units that have been used.", to: "/packages",
            },
          ];
          const activityState = (cu.new_this_month || 0) > 0 ? "Growing" : (bk.today || 0) + (bk.upcoming || 0) > 0 ? "Active" : (cu.total || 0) > 0 ? "Quiet" : "Limited data";

          const RatioRow = (dm) => {
            const isOpen = open === dm.key;
            return (
              <div key={dm.key} className="rounded-2xl border border-pawkt-border-soft overflow-hidden">
                <button data-testid={`pulse-${dm.key}`} onClick={() => setOpen(isOpen ? null : dm.key)} aria-expanded={isOpen}
                  className="w-full px-4 py-3 text-left hover:bg-pawkt-soft transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-bold text-pawkt-text">{dm.label}</span>
                    <span className="flex items-center gap-2">
                      {dm.value !== null ? <span className="text-sm font-extrabold tabular text-pawkt-primary-deep">{dm.value}%</span> : <StateChip state="Limited data" />}
                      <ChevronDown className={cn("h-4 w-4 text-pawkt-text-disabled transition-transform", isOpen && "rotate-180")} />
                    </span>
                  </div>
                  {dm.value !== null && (
                    <div className="mt-2 h-2 rounded-full bg-pawkt-elevated overflow-hidden" role="progressbar" aria-valuenow={dm.value} aria-valuemin={0} aria-valuemax={100}>
                      <div className="h-full rounded-full pawkt-gradient-primary transition-[width] duration-700 ease-out" style={{ width: `${dm.value}%` }} />
                    </div>
                  )}
                  <div className="mt-1.5 text-xs text-pawkt-text-secondary">{dm.caption}</div>
                </button>
                {isOpen && (
                  <div className="px-4 pb-3 pt-1 bg-pawkt-soft animate-fade-in">
                    <p className="text-xs text-pawkt-text-secondary">{dm.detail}</p>
                    <button onClick={() => navigate(dm.to)} className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-pawkt-primary-deep hover:underline">
                      Open details <ArrowRight className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            );
          };

          const mOpen = open === "momentum";
          const capacityState = (bk.today || 0) >= 6 ? "Busy" : (bk.today || 0) + (bk.upcoming || 0) > 0 ? "Active" : "Quiet";
          const visibleRatios = lowData ? ratios.filter((r) => r.value !== null) : ratios;
          return (
            <div className="space-y-3">
              {/* prominent operational signals (incl. Today's capacity) */}
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between rounded-2xl bg-pawkt-soft border border-pawkt-border-soft px-4 py-3" data-testid="pulse-capacity">
                  <span className="text-sm font-bold text-pawkt-text">Today's capacity</span>
                  <StateChip state={capacityState} />
                </div>
                <div className="flex items-center justify-between rounded-2xl bg-pawkt-soft border border-pawkt-border-soft px-4 py-3" data-testid="pulse-activity">
                  <span className="text-sm font-bold text-pawkt-text">Customer activity</span>
                  <StateChip state={activityState} />
                </div>
              </div>

              {lowData && (
                <p className="rounded-2xl bg-pawkt-primary-soft/60 border border-pawkt-border-soft px-4 py-3 text-xs text-pawkt-text-secondary" data-testid="pulse-lowdata">
                  <span className="font-bold text-pawkt-text">Your business is just getting started.</span> As Pawkt learns your business, your Pulse will become more detailed.
                </p>
              )}

              {/* 14-day revenue trend + momentum */}
              <div className="rounded-2xl border border-pawkt-border-soft bg-pawkt-soft overflow-hidden">
                <button data-testid="pulse-momentum" onClick={() => setOpen(mOpen ? null : "momentum")} aria-expanded={mOpen}
                  className="w-full px-4 pt-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-pawkt-text-secondary">14-day revenue trend</span>
                    {momentum.growth !== null ? (
                      <span className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-extrabold",
                        momentum.growth >= 0 ? "bg-pawkt-mint text-pawkt-success-deep" : "bg-pawkt-coral text-pawkt-emergency")}>
                        {momentum.growth >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                        {momentum.growth >= 0 ? "+" : ""}{momentum.growth}% vs last month
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-pawkt-elevated px-2.5 py-0.5 text-[11px] font-bold text-pawkt-text-secondary">Limited data</span>
                    )}
                  </div>
                </button>
                <div className="px-2"><Sparkline points={d.trend} /></div>
                {mOpen && (
                  <div className="px-4 pb-3 pt-1 animate-fade-in">
                    <p className="text-xs text-pawkt-text-secondary">{momentum.detail}</p>
                    <button onClick={() => navigate(momentum.to)} className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-pawkt-primary-deep hover:underline">
                      Open revenue <ArrowRight className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>

              {visibleRatios.map(RatioRow)}

              {!canReports && <p className="text-xs text-pawkt-text-disabled">Some signals require reporting access.</p>}
            </div>
          );
        }}
      </SectionShell>
    </Panel>
  );
}
